import React, { useRef, useState } from 'react';
import Zoom from 'react-medium-image-zoom';

import { Box, IconButton, Stack } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import ShopConfig from '../../common/api/config/shopConfig';
import QVContainer from '../../common/ui/qvshop/components/QVContainer';

export default function ProductImageGallery({ images }) {
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const thumbnailsRef = useRef(null);

    if (!images || images.length === 0) {
        return null;
    }

    const hasPreviousImage = selectedImageIndex > 0;
    const hasNextImage = selectedImageIndex < images.length - 1;
    const selectedImage = images[selectedImageIndex];

    const showPreviousImage = () => {
        if (hasPreviousImage) {
            setSelectedImageIndex(selectedImageIndex - 1);
        }
    };

    const showNextImage = () => {
        if (hasNextImage) {
            setSelectedImageIndex(selectedImageIndex + 1);
        }
    };

    const scrollThumbnails = (direction) => {
        if (!thumbnailsRef.current) {
            return;
        }

        thumbnailsRef.current.scrollBy({
            left: direction * 112,
            behavior: 'smooth'
        });
    };

    return (
        <QVContainer
            borderRadius={8}
            padding={0}
            sx={{
                width: { xs: '100%', lg: '50%', xl: 500 },
                minWidth: { lg: 360, xl: 500 },
                maxWidth: '100%',
                height: { lg: 'var(--product-details-top-box-height)' },
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    height: { xs: 300, sm: 350, md: 400, lg: 'auto' },
                    flex: { lg: '1 1 auto' },
                    minHeight: { lg: 0 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 5,
                    py: 3
                }}
            >
                {images.length > 1 && (
                    <IconButton
                        aria-label="Vorheriges Bild"
                        onClick={showPreviousImage}
                        disabled={!hasPreviousImage}
                        sx={{
                            position: 'absolute',
                            left: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            bgcolor: 'rgba(255, 255, 255, 0.85)',
                            boxShadow: 1,
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.95)'
                            }
                        }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                )}

                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '& [data-rmiz]': {
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        },
                        '& [data-rmiz-content]': {
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        },
                        '& [data-rmiz-btn-zoom]': {
                            width: '100%',
                            height: '100%'
                        }
                    }}
                >
                    <Zoom zoomMargin={20} zoomImg={{ src: ShopConfig.config().getURLForZoomedProductImage(selectedImage) }}>
                        <Box
                            component="img"
                            alt=""
                            src={ShopConfig.config().getURLForProductImage(selectedImage)}
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                objectPosition: 'center',
                                cursor: 'zoom-in'
                            }}
                        />
                    </Zoom>
                </Box>

                {images.length > 1 && (
                    <IconButton
                        aria-label="Nächstes Bild"
                        onClick={showNextImage}
                        disabled={!hasNextImage}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            bgcolor: 'rgba(255, 255, 255, 0.85)',
                            boxShadow: 1,
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.95)'
                            }
                        }}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                )}
            </Box>

            {images.length > 1 && (
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                        px: 2,
                        pb: 2
                    }}
                >
                    <IconButton size="small" aria-label="Vorschaubilder nach links" onClick={() => scrollThumbnails(-1)}>
                        <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <Box
                        ref={thumbnailsRef}
                        sx={{
                            display: 'flex',
                            gap: 1,
                            overflowX: 'auto',
                            scrollBehavior: 'smooth',
                            flex: '1 1 auto',
                            py: 0.5,
                            scrollbarWidth: 'none',
                            '&::-webkit-scrollbar': {
                                display: 'none'
                            }
                        }}
                    >
                        {images.map((image, imageIndex) => (
                            <Box
                                component="button"
                                key={`${image}-${imageIndex}`}
                                onClick={() => setSelectedImageIndex(imageIndex)}
                                sx={{
                                    width: 72,
                                    height: 72,
                                    p: 0.5,
                                    borderRadius: 1,
                                    border: (theme) => imageIndex === selectedImageIndex ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                                    bgcolor: 'background.paper',
                                    cursor: 'pointer',
                                    flex: '0 0 auto'
                                }}
                            >
                                <Box
                                    component="img"
                                    alt=""
                                    src={ShopConfig.config().getURLForProductImage(image)}
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        objectPosition: 'center'
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>
                    <IconButton size="small" aria-label="Vorschaubilder nach rechts" onClick={() => scrollThumbnails(1)}>
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </Stack>
            )}
        </QVContainer>
    );
}
